import _ from 'lodash';
import React from 'react';

import {
  Box,
  Button,
  Grid,
  IconButton,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {
  fields,
  LoadingOverlay,
  modalUtil
} from 'iron-forge-react-modules';
import {
  routeDirectory,
  selectFields
} from 'modules';
import {
  useForm,
  useWatch
} from 'react-hook-form';
import {
  useNavigate,
  useSearchParams
} from 'react-router-dom';

import { Archive, Clear, Edit } from '@mui/icons-material';
import * as dataService from './dataService';
import * as EditWriteRequestModal from './EditWriteRequestModal/EditWriteRequestModal';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const { genController } = selectFields.StoreSelectField.genComponentAndControllerGenerator();
const ControlledStoreSelectField = genController<types.IFormState>();

const FormBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-start'
}));



export const FC: React.FC = () => {
  const navigate = useNavigate();
  const form = useForm<types.IFormState>({
    defaultValues: {
      searchText: '',
      store: null
    }
  });
  const watch = useWatch({ control: form.control });
  const queryTuple = dataService.useQuery({
    searchText: watch.searchText ?? '',
    store: watch.store ? {
      ...watch.store,
      id: watch.store.id ?? '',
      displayName: watch.store.displayName ?? ''
    } : null
  });

  const {
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['writeRequestEditor']);
  const [searchParams] = useSearchParams();
  const [
    clearValidationErrors,
    clearValidationErrorsTuple
  ] = dataService.useClearValidationErrors();

  const [
    archiveWriteRequest
  ] = dataService.useArchiveWriteRequest();



  React.useEffect(
    () => {
      const writeRequestId = searchParams.get('writeRequestId');
      if (writeRequestId && !modalType2TargetId.writeRequestEditor) {
        setModalIsOpen('writeRequestEditor', writeRequestId);
      }
    },
    [
      modalType2TargetId.writeRequestEditor,
      searchParams,
      setModalIsOpen
    ]
  );

  const storeId = useWatch({ control: form.control });



  return (
    <>
      <LoadingOverlay.FC active={queryTuple.loading} />

      <FormBox>
        <Grid
          container
          spacing={1}>
          <Grid
            item
            xs={3}>
            <ControlledTextField
              label="Search text"
              isDisabled={queryTuple.loading}
              fieldKey="searchText"
              formControl={form.control} />
          </Grid>

          <Grid
            item
            xs={3}>
            <ControlledStoreSelectField
              label="Store"
              isDisabled={queryTuple.loading}
              fieldKey="store"
              formControl={form.control} />
          </Grid>

          <Grid
            item
            xs={3}>
            <Button
              type="submit"
              variant="contained">
              Submit
            </Button>
          </Grid>

          <Grid
            item
            xs={3}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between'
            }}>
            <Button
              sx={{
                whiteSpace: 'nowrap',
                p: 0
              }}
              disabled={clearValidationErrorsTuple.loading || queryTuple.loading}
              onClick={async () => {
                await clearValidationErrors(storeId.store?.id);
                queryTuple.refetch?.();
              }}>
              Clear All Sales Order Errors
            </Button>

            <Typography>
              <b>{queryTuple.data.writeRequests.length}</b> errors
            </Typography>
          </Grid>
        </Grid>
      </FormBox>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Request Format</TableCell>
            <TableCell>Email/Order/Return #</TableCell>
            <TableCell>Errors</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {queryTuple.loading ? (
            _.times(10, () => (
              <TableRow>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
                <TableCell><Skeleton /></TableCell>
              </TableRow>
            ))

          ) : queryTuple.data.writeRequests.map(writeRequest => (
            <TableRow key={writeRequest.id}>
              <TableCell>
                {writeRequest.format}
              </TableCell>

              <TableCell>
                {writeRequest.identifier}
              </TableCell>


              <TableCell>
                {_.join(writeRequest.validationErrors, '\n')}
              </TableCell>

              <TableCell>
                <Tooltip title="Edit this write request" leaveDelay={0}>
                  <IconButton
                    size="small"
                    sx={{ padding: 0 }}
                    style={{ marginRight: 20 }}
                    onClick={() => navigate(routeDirectory.admin.orderManagement.path('validationErrors', writeRequest.id), { replace: true })}>
                    <Edit />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Clear validation errors" leaveDelay={0}>
                  <IconButton
                    size="small"
                    sx={{ p: 0 }}
                    color="error"
                    style={{ marginRight: 20 }}
                    onClick={async () => {
                      await clearValidationErrors(storeId.store?.id, writeRequest.id);
                      queryTuple.refetch?.();
                    }}>
                    <Clear />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Archive request validation errors" leaveDelay={0}>
                  <IconButton
                    size="small"
                    sx={{ p: 0 }}
                    color="error"
                    onClick={async () => {
                      await archiveWriteRequest(writeRequest.id);
                      queryTuple.refetch?.();
                    }}>
                    <Archive />
                  </IconButton>
                </Tooltip>

              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {modalType2TargetId.writeRequestEditor && (
        <EditWriteRequestModal.FC
          writeRequestId={modalType2TargetId.writeRequestEditor}
          onClose={() => {
            navigate(routeDirectory.admin.orderManagement.path('validationErrors'));
            setModalIsOpen('writeRequestEditor', false);
          }}
          onSaveWriteRequest={() => {
            navigate(routeDirectory.admin.orderManagement.path('validationErrors'));
            setModalIsOpen('writeRequestEditor', false);
          }} />
      )}
    </>
  );
};

